import type { AnalysisFeature, GroupId } from '@/features/types';
import { Rating } from '@/features/types';
import { getSpanCategories, getCategoryIndex } from '@/views/map/Legend/utils';
import type {
  FuelHazardRating,
  GroupSummary,
} from '@/generated/types/navigator/types.gen';

import {
  GroupAnalysisType,
  WorkLogStatus,
} from '@/generated/types/navigator/types.gen';

export type RGB = [number, number, number];
type RGBA = [number, number, number, number];
type Color = RGBA | RGB;

/**
 * @returns An object, mapping each threshold value to a color in desc threshold value order
 * e.g. { 40: [rgb array], 20: [rgb array] }
 *
 * If `thresholds` is longer than `colors`, the extra threshold keys would otherwise
 * resolve to `undefined`, which downstream collapses to a transparent color and
 * makes tiles invisible. Cap iteration at `colors.length` so every produced key has
 * a real color.
 */
const mapThresholdsToColors = (thresholds: number[], colors: Color[]) => {
  const result: Record<number, Color> = {};

  const len = Math.min(thresholds.length, colors.length);
  for (let i = 0; i < len; i++) {
    result[thresholds[i]] = colors[i];
  }

  return result;
};

// grayBrand[6] #6D6D6D. Carries both spans with no rating and the whole network under
// the fall-in objective, which has no per-span score to colour by.
export const UNKNOWN: RGB = [109, 109, 109];

export const DEFAULT_CATEGORY_THRESHOLD = [0, 1, 3, 5];

export const WORKSTATUS_COLOR: Record<string, RGBA> = {
  fillGoodStatus: [49, 226, 188, 255],
  zoomedInFillGoodStatus: [49, 226, 188, 100],
  fillBadStatus: [255, 42, 132, 255],
  zoomedInFillBadStatus: [255, 42, 132, 100],
};

export const OPAQUE_ALPHA = 255;
export const TRANSLUCENT_ALPHA = 150;
export const FAINT_ALPHA = 50;
export const TRANSPARENT_ALPHA = 0;

export const ENC_RISK_COLOUR: Record<string, RGB> = {
  high: [220, 27, 26],
  medium: [242, 170, 2],
  low: [250, 225, 0],
  minor: [140, 140, 140],
  none: [12, 197, 255],
  unknown: UNKNOWN,
} as const;

export const PRIORITY_COLOUR: Record<string, RGB> = {
  P1: [227, 51, 51],
  P2: [253, 133, 46],
  P3: [253, 191, 46],
  P4: [180, 216, 251],
  P5: [87, 197, 242],
  unknown: UNKNOWN,
} as const;

const RISK_COLOUR: Record<string, RGB> = {
  high: [227, 51, 51],
  medium: [253, 133, 46],
  low: [253, 191, 46],
  minor: [144, 144, 144],
  none: [87, 197, 242],
  unknown: UNKNOWN,
};

const FUEL_HAZARD_RATING_COLOUR: Record<FuelHazardRating, RGB> = {
  [Rating.None]: RISK_COLOUR.none,
  [Rating.Minor]: RISK_COLOUR.minor,
  [Rating.Low]: RISK_COLOUR.low,
  [Rating.Medium]: RISK_COLOUR.medium,
  [Rating.High]: RISK_COLOUR.high,
};

const WORKLOAD_ANALYSIS_COLOURS: Record<string, RGB> = {
  Q1: [180, 216, 251],
  Q2: [248, 178, 178],
  Q3: [241, 127, 127],
  Q4: [227, 51, 51],
};

const CLEAR_COLOURS: Record<string, RGB> = {
  high: [53, 148, 237],
  medium: [129, 190, 246],
  low: [247, 247, 247],
  minor: [201, 201, 201],
};

export const getAnalysisColors = (
  selectedNetworkGroupId: GroupId | null,
  spanNetworkGroupId: GroupId | null,
  encroachmentThresholds: number[],
  priorityThresholds: number[],
  wildfirePriorityThresholds: number[],
  criticalTreesThresholds: number[],
  clearThresholds: number[],
  activeGroupName: string | null
) => {
  const colorMapping: Record<string, Record<number, Color> | undefined> = {};
  const isSpan =
    selectedNetworkGroupId === spanNetworkGroupId || activeGroupName;

  const encColors = isSpan
    ? [
        RISK_COLOUR.none,
        RISK_COLOUR.minor,
        RISK_COLOUR.low,
        RISK_COLOUR.medium,
        RISK_COLOUR.high,
      ]
    : [RISK_COLOUR.minor, RISK_COLOUR.low, RISK_COLOUR.high];

  const prioColors = isSpan
    ? [
        PRIORITY_COLOUR.unknown,
        PRIORITY_COLOUR.P5,
        PRIORITY_COLOUR.P4,
        PRIORITY_COLOUR.P3,
        PRIORITY_COLOUR.P2,
        PRIORITY_COLOUR.P1,
      ]
    : [RISK_COLOUR.minor, RISK_COLOUR.low, RISK_COLOUR.high];

  const workloadColors = [
    WORKLOAD_ANALYSIS_COLOURS.Q1,
    WORKLOAD_ANALYSIS_COLOURS.Q2,
    WORKLOAD_ANALYSIS_COLOURS.Q3,
    WORKLOAD_ANALYSIS_COLOURS.Q4,
  ];

  const clearColors = isSpan
    ? [CLEAR_COLOURS.minor, CLEAR_COLOURS.high]
    : [
        CLEAR_COLOURS.minor,
        CLEAR_COLOURS.low,
        CLEAR_COLOURS.medium,
        CLEAR_COLOURS.high,
      ];

  const encThresholds = isSpan
    ? getSpanCategories(GroupAnalysisType.ENCROACHMENT)
    : encroachmentThresholds;

  const prioThresholds = isSpan
    ? getSpanCategories(GroupAnalysisType.PRIORITY)
    : priorityThresholds;

  const wildfirePrioThresholds = isSpan
    ? getSpanCategories(GroupAnalysisType.WILDFIRE_PRIORITY)
    : wildfirePriorityThresholds;

  const clrThresholds = isSpan
    ? getSpanCategories(GroupAnalysisType.CLEAR)
    : clearThresholds;

  colorMapping[GroupAnalysisType.ENCROACHMENT] =
    encThresholds && mapThresholdsToColors(encThresholds, encColors);

  colorMapping[GroupAnalysisType.PRIORITY] =
    prioThresholds && mapThresholdsToColors(prioThresholds, prioColors);

  colorMapping[GroupAnalysisType.WILDFIRE_PRIORITY] =
    wildfirePrioThresholds &&
    mapThresholdsToColors(wildfirePrioThresholds, prioColors);

  colorMapping[GroupAnalysisType.WORKLOAD_ANALYSIS] = mapThresholdsToColors(
    criticalTreesThresholds,
    workloadColors
  );

  colorMapping[GroupAnalysisType.CLEAR] =
    clrThresholds && mapThresholdsToColors(clrThresholds, clearColors);

  return colorMapping;
};

export const getAnalysisColor = (
  d: AnalysisFeature,
  analysisColors: Record<string, Record<number, Color> | undefined>,
  selectedAnalysis: GroupAnalysisType,
  groupSummary: Map<string, GroupSummary>,
  isSpan: boolean,
  hasGroupRiskSummary: boolean = false
): Color => {
  let total: number | null = null;
  let colors: Record<number, Color> | undefined = {};

  if (selectedAnalysis === GroupAnalysisType.FALL_IN) {
    return [...UNKNOWN];
  }

  if (!d.properties.group) {
    return [...UNKNOWN];
  }

  // total is available in d.properties for spans and in groupSummary for higher-order network groups
  switch (selectedAnalysis) {
    case GroupAnalysisType.ENCROACHMENT:
      total =
        d.properties.totalEncScore ??
        groupSummary.get(d.properties.group)?.totalEncScore ??
        null;
      colors = analysisColors[GroupAnalysisType.ENCROACHMENT];
      break;
    case GroupAnalysisType.PRIORITY:
      total =
        d.properties.totalPriorityScore ??
        groupSummary.get(d.properties.group)?.totalPriorityScore ??
        null;
      colors = analysisColors[GroupAnalysisType.PRIORITY];
      break;
    case GroupAnalysisType.WILDFIRE_PRIORITY:
      // In `wildfire-mode = all` the network-group thresholds come from
      // `categoriesByWildfireRisk` (dollar-scale), so the lookup must use
      // `wildfireRisk` to match. Other modes keep the score-scale lookup.
      total =
        !isSpan && hasGroupRiskSummary
          ? (d.properties.wildfireRisk ??
            groupSummary.get(d.properties.group)?.wildfireRisk ??
            null)
          : (d.properties.totalWildfirePriorityScore ??
            groupSummary.get(d.properties.group)?.totalWildfirePriorityScore ??
            null);
      colors = analysisColors[GroupAnalysisType.WILDFIRE_PRIORITY];
      break;
    case GroupAnalysisType.WORKLOAD_ANALYSIS:
      total =
        d.properties.criticalTreesCount ??
        groupSummary.get(d.properties.group)?.criticalTreesCount ??
        null;
      colors = analysisColors[GroupAnalysisType.WORKLOAD_ANALYSIS];
      break;
    case GroupAnalysisType.CLEAR:
      total =
        d.properties.clearPercentage ??
        groupSummary.get(d.properties.group)?.clearPercentage ??
        null;
      colors = analysisColors[GroupAnalysisType.CLEAR];
      break;
  }

  if (!colors) return UNKNOWN;

  // 0 can mean no critical trees (not unknown), no encroachment
  if (total === null || total < 0) return UNKNOWN;

  // An array of threshold values
  const keys = Object.keys(colors)
    .map(Number)
    .sort((a, b) => a - b);

  // Workload thresholds are count floors (a span with 6 critical trees belongs to
  // the `5` bucket, not the next one up), so it must use group-counting even for
  // spans. The score-based span scales intentionally round up to the next band.
  const trailing =
    isSpan && selectedAnalysis !== GroupAnalysisType.WORKLOAD_ANALYSIS;
  const categoryIndex = getCategoryIndex(keys, total, trailing);

  // The threshold value we need to access for the correct colors
  const key = keys[categoryIndex];

  return colors[key] ?? [0, 0, 0, 0];
};

export const getFuelHazardColor = (d: AnalysisFeature): RGB => {
  const rating = d.properties.fuelHazardRating;
  return rating == null ? UNKNOWN : FUEL_HAZARD_RATING_COLOUR[rating];
};

export const getFuelHazardFillColor = (
  d: AnalysisFeature,
  isOverview: boolean
): RGBA => {
  const lowRiskAlpha = isOverview ? TRANSLUCENT_ALPHA : TRANSPARENT_ALPHA;
  const highRiskAlpha = isOverview ? OPAQUE_ALPHA : TRANSPARENT_ALPHA;
  const unknownAlpha = isOverview ? FAINT_ALPHA : TRANSPARENT_ALPHA;
  if (d.properties.workStatus) {
    return workStatusColorEstimation(d.properties.workStatus, isOverview);
  }
  const rating = d.properties.fuelHazardRating;
  if (rating == null) {
    return [...UNKNOWN, unknownAlpha];
  }
  return [
    ...FUEL_HAZARD_RATING_COLOUR[rating],
    rating >= Rating.Medium ? highRiskAlpha : lowRiskAlpha,
  ];
};

export const workStatusColorEstimation = (
  workStatus: WorkLogStatus,
  isOverview: boolean
): RGBA => {
  const isWorkNeeded = workStatus === WorkLogStatus.WORK_NEEDED;

  if (isOverview) {
    return isWorkNeeded
      ? WORKSTATUS_COLOR.fillBadStatus
      : WORKSTATUS_COLOR.fillGoodStatus;
  }
  return isWorkNeeded
    ? WORKSTATUS_COLOR.zoomedInFillBadStatus
    : WORKSTATUS_COLOR.zoomedInFillGoodStatus;
};

export const sanitizeTestId = (label: string) =>
  label.toLowerCase().replace(/\s+/g, '-');

export const getWildfireRiskFillColor = (
  d: AnalysisFeature,
  analysisColors: Record<string, Record<number, Color> | undefined>,
  groupSummary: Map<string, GroupSummary>,
  isOverview: boolean
): Color => {
  if (d.properties.workStatus) {
    return workStatusColorEstimation(d.properties.workStatus, isOverview);
  }
  const [r, g, b] = getAnalysisColor(
    d,
    analysisColors,
    GroupAnalysisType.WILDFIRE_PRIORITY,
    groupSummary,
    false,
    true
  );
  return [r, g, b, isOverview ? OPAQUE_ALPHA : TRANSPARENT_ALPHA];
};

export const getPriorityFillColor = (
  totalPriorityScore: number | null | undefined,
  workStatus: WorkLogStatus | null | undefined,
  isOverview: boolean
): [number, number, number, number] => {
  if (workStatus) {
    return workStatusColorEstimation(workStatus, isOverview);
  }
  switch (totalPriorityScore) {
    case 40:
      return [
        ...PRIORITY_COLOUR['P1'],
        isOverview ? OPAQUE_ALPHA : TRANSPARENT_ALPHA,
      ];
    case 20:
      return [
        ...PRIORITY_COLOUR['P2'],
        isOverview ? OPAQUE_ALPHA : TRANSPARENT_ALPHA,
      ];
    case 10:
      return [
        ...PRIORITY_COLOUR['P3'],
        isOverview ? TRANSLUCENT_ALPHA : TRANSPARENT_ALPHA,
      ];
    case 5:
      return [
        ...PRIORITY_COLOUR['P4'],
        isOverview ? TRANSLUCENT_ALPHA : TRANSPARENT_ALPHA,
      ];
    case 2:
      return [
        ...PRIORITY_COLOUR['P5'],
        isOverview ? TRANSLUCENT_ALPHA : TRANSPARENT_ALPHA,
      ];
    default:
      return [...UNKNOWN, isOverview ? FAINT_ALPHA : TRANSPARENT_ALPHA];
  }
};
